import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import WavingHandOutlinedIcon from "@mui/icons-material/WavingHandOutlined";
import { useTranslation } from "react-i18next";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogPrimaryButton,
  AppDialogTitle,
} from "../dialog";
import { AppButton, AppRadioGroup, AppTextField } from "../form";
import { AppFormField, AppFormGrid } from "../layout";
import { GRID_FULL } from "../../lib/ui/formLayout";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { dashboardService } from "../../services/dashboardService";
import type { ReportDashboard } from "../../types/dashboard";
import type { DateRangePreset } from "../DateRangeHeader";
import {
  DASHBOARD_LAYOUT_PRESETS,
  type DashboardLayoutPresetId,
} from "../../utils/dashboardLayoutPresets";

export const ONBOARDING_STORAGE_KEY = "aireport.onboarding.completed";
export const ONBOARDING_DATE_PRESET_KEY = "aireport.onboarding.datePreset";

export function isOnboardingComplete(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_STORAGE_KEY) === "1";
  } catch {
    return true;
  }
}

export function markOnboardingComplete(): void {
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
}

type Props = {
  open: boolean;
  onClose: () => void;
};

const STEPS = ["welcome", "date", "dashboard"] as const;
type StepId = (typeof STEPS)[number];

const DATE_PRESETS: DateRangePreset[] = ["last7", "last30", "thisMonth"];

export default function OnboardingDialog({ open, onClose }: Props) {
  const custom = useAppCustomStyles();
  const styles = custom.components.onboardingDialog;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const step: StepId = STEPS[stepIndex];
  const [datePreset, setDatePreset] = useState<DateRangePreset>("last7");
  const [dashboards, setDashboards] = useState<ReportDashboard[]>([]);
  const [loadingDashboards, setLoadingDashboards] = useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [newDashboardName, setNewDashboardName] = useState("");
  const [layoutPresetId, setLayoutPresetId] = useState<DashboardLayoutPresetId>("executive");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open || step !== "dashboard") return;
    setLoadingDashboards(true);
    dashboardService
      .list(0, 50)
      .then((page) => setDashboards(page.content || []))
      .catch(() => setDashboards([]))
      .finally(() => setLoadingDashboards(false));
  }, [open, step]);

  const finish = useCallback(
    async (dashboardId?: string | null) => {
      try {
        localStorage.setItem(ONBOARDING_DATE_PRESET_KEY, datePreset);
      } catch {
        /* ignore */
      }
      markOnboardingComplete();
      onClose();
      if (dashboardId) {
        navigate(`/dashboards/${dashboardId}`);
      } else {
        navigate("/dashboards");
      }
    },
    [datePreset, navigate, onClose]
  );

  const createAndFinish = async () => {
    if (!newDashboardName.trim()) return;
    setCreating(true);
    try {
      const preset = DASHBOARD_LAYOUT_PRESETS.find((p) => p.id === layoutPresetId)
        ?? DASHBOARD_LAYOUT_PRESETS[0];
      const created = await dashboardService.create({
        name: newDashboardName.trim(),
        layout: {
          gridCols: preset.gridCols,
          rowHeight: preset.rowHeight,
          widgets: [],
          layoutPresetId: preset.id,
          zones: preset.zones,
        },
        defaults: {
          defaultDateRange:
            datePreset === "last30" ? "last_30d" : datePreset === "thisMonth" ? "this_month" : "last_7d",
        },
      });
      await finish(created.id);
    } finally {
      setCreating(false);
    }
  };

  const stepLabels = [
    t("onboarding.step.welcome"),
    t("onboarding.step.date"),
    t("onboarding.step.dashboard"),
  ];

  const datePresetLabel = (p: DateRangePreset) => {
    switch (p) {
      case "last30":
        return t("dateRange.last30days");
      case "thisMonth":
        return t("dateRange.thisMonth");
      default:
        return t("dateRange.last7days");
    }
  };

  return (
    <AppDialog open={open} onClose={() => {}} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <AppDialogTitle>{t("onboarding.title")}</AppDialogTitle>
      <AppDialogContent>
        <Stepper activeStep={stepIndex} alternativeLabel sx={styles.stepper}>
          {stepLabels.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === "welcome" && (
          <Stack spacing={2} alignItems="center" textAlign="center" sx={styles.welcomeStack}>
            <WavingHandOutlinedIcon sx={styles.welcomeIcon} />
            <Typography variant="h6" fontWeight={700}>
              {t("onboarding.welcome.headline")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("onboarding.welcome.body")}
            </Typography>
          </Stack>
        )}

        {step === "date" && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EventOutlinedIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                {t("onboarding.date.headline")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t("onboarding.date.body")}
            </Typography>
            <AppRadioGroup
              label={t("onboarding.date.headline")}
              value={datePreset}
              onChange={(v) => setDatePreset(v as DateRangePreset)}
              row={false}
              options={DATE_PRESETS.map((p) => ({ value: p, label: datePresetLabel(p) }))}
            />
          </Stack>
        )}

        {step === "dashboard" && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DashboardOutlinedIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                {t("onboarding.dashboard.headline")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t("onboarding.dashboard.body")}
            </Typography>

            {loadingDashboards ? (
              <Box sx={styles.loadingBox}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Stack spacing={1}>
                {dashboards.map((d) => (
                  <Card
                    key={d.id}
                    variant="outlined"
                    sx={styles.dashboardCard(selectedDashboardId === d.id)}
                  >
                    <CardActionArea onClick={() => setSelectedDashboardId(d.id || null)}>
                      <CardContent sx={styles.cardContent}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {d.name}
                        </Typography>
                        {d.description && (
                          <Typography variant="caption" color="text.secondary">
                            {d.description}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            )}

            <Typography variant="subtitle2" fontWeight={700} sx={styles.orCreate}>
              {t("onboarding.dashboard.orCreate")}
            </Typography>
            <AppFormGrid>
              <AppFormField size={GRID_FULL}>
                <AppTextField
                  label={t("dashboard.name")}
                  value={newDashboardName}
                  onChange={(e) => {
                    setNewDashboardName(e.target.value);
                    setSelectedDashboardId(null);
                  }}
                  fullWidth
                />
              </AppFormField>
              <AppFormField size={GRID_FULL}>
                <AppRadioGroup
                  label={t("dashboard.preset.label")}
                  value={layoutPresetId}
                  onChange={(v) => setLayoutPresetId(v as DashboardLayoutPresetId)}
                  row={false}
                  options={DASHBOARD_LAYOUT_PRESETS.filter((p) => p.id !== "blank").map((p) => ({
                    value: p.id,
                    label: t(p.nameKey),
                  }))}
                />
              </AppFormField>
            </AppFormGrid>
          </Stack>
        )}
      </AppDialogContent>

      <AppDialogActions>
        {stepIndex > 0 && (
          <AppDialogCancelButton onClick={() => setStepIndex((i) => i - 1)}>
            {t("onboarding.back")}
          </AppDialogCancelButton>
        )}
        {step === "welcome" && (
          <AppDialogPrimaryButton onClick={() => setStepIndex(1)}>
            {t("onboarding.next")}
          </AppDialogPrimaryButton>
        )}
        {step === "date" && (
          <AppDialogPrimaryButton onClick={() => setStepIndex(2)}>
            {t("onboarding.next")}
          </AppDialogPrimaryButton>
        )}
        {step === "dashboard" && (
          <>
            <AppButton variant="outlined" onClick={() => finish(null)}>
              {t("onboarding.skip")}
            </AppButton>
            {selectedDashboardId ? (
              <AppDialogPrimaryButton onClick={() => finish(selectedDashboardId)}>
                {t("onboarding.openDashboard")}
              </AppDialogPrimaryButton>
            ) : (
              <AppDialogPrimaryButton
                onClick={createAndFinish}
                disabled={!newDashboardName.trim() || creating}
              >
                {creating ? t("common.saving") : t("onboarding.createAndOpen")}
              </AppDialogPrimaryButton>
            )}
          </>
        )}
      </AppDialogActions>
    </AppDialog>
  );
}
